import { useAddCustomerContactMutation } from '@/dealers/_gen/gql'
import ModalForm from '@/gf/components/ModalForm'
import Field from '@/gf/components/next/forms/Field'
import PhoneInput from '@/gf/components/next/forms/PhoneInput'
import TextInput from '@/gf/components/next/forms/TextInput'
import useMsgs from '@/gf/hooks/useMsgs'
import { yupResolver } from '@/gf/lib/yupResolver'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

interface ContactForm {
  name: string
  email: string
  phoneNumber: string
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Contact Name is required'),
  email: Yup.string().email('Must be a valid email address').required('Email is required'),
  phoneNumber: Yup.string().label('Phone Number'),
})

// The two rejections the seller can fix by editing the form belong under the
// Email field; everything else — a claimed account, an account that isn't this
// store's — is a toast, because there is nothing in the form to correct.
const FIELD_ERROR_CODES = ['email_taken', 'already_a_contact']

interface GqlError {
  message?: unknown
  code?: unknown
  extensions?: { code?: unknown }
}

// Absinthe merges an error map's extra keys onto the top-level error object, and
// some setups nest them under `extensions` — check both, the same way
// AccountMachine does.
const firstError = (err: unknown): { message: string; code: string | null } | null => {
  const gqlErrors = (err as { graphQLErrors?: readonly GqlError[] })?.graphQLErrors
  const gqlError = gqlErrors?.[0]
  if (!gqlError) return null

  const code = gqlError.code ?? gqlError.extensions?.code
  return {
    message: String(gqlError.message ?? ''),
    code: typeof code === 'string' ? code : null,
  }
}

const AddContactModal = ({
  customerAccountId,
  customerName,
  open,
  onClose,
  onAdded,
}: {
  customerAccountId: string
  customerName: string
  open: boolean
  onClose: () => void
  onAdded: () => void
}) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [addCustomerContact, { loading }] = useAddCustomerContactMutation()

  // No `shouldUnregister` — the option is what broke this form. In
  // react-hook-form 7.41.0 it makes `_formValues` start as `{}` rather than a
  // copy of `defaultValues`, and `_getWatch` reads `_formValues` once the form
  // is mounted without falling back to the defaults. This modal is mounted on
  // page load with `open={false}`, so by the time the click renders the phone
  // `Controller` for the first time, its field reads `undefined` instead of
  // `''` (GEA-8026). Nothing here needs it: there are no conditional fields,
  // and `close()` already calls `form.reset()`.
  const form = useForm<ContactForm>({
    defaultValues: { name: '', email: '', phoneNumber: '' },
    resolver: yupResolver(validationSchema),
  })

  const close = () => {
    form.reset()
    onClose()
  }

  const onSubmit = async (values: ContactForm) => {
    if (loading) return

    try {
      const result = await addCustomerContact({
        variables: {
          customerAccountId,
          name: values.name,
          email: values.email,
          phoneNumber: values.phoneNumber || null,
        },
      })

      const contact = result.data?.addCustomerContact
      if (!contact) return

      close()
      onAdded()
      msgsMgr.add(`${contact.name} was added to ${customerName}.`, 'positive')
    } catch (err) {
      const error = firstError(err)

      if (error?.code && FIELD_ERROR_CODES.includes(error.code)) {
        form.setError('email', { message: error.message })
      } else if (error?.message) {
        msgsMgr.add(error.message, 'negative')
      } else {
        msgsMgr.addUnknownError()
      }
    }
  }

  return (
    <ModalForm
      open={open}
      onClose={close}
      onSubmit={form.handleSubmit(onSubmit)}
      title="Add Contact"
      submitButtonText="Save"
      submitButtonShowSpinner={loading}
      submitButtonDisabled={loading}
    >
      <div className="flex flex-col gap-y-2">
        <Field label="Contact Name" error={form.formState.errors.name?.message}>
          <TextInput autoFocus {...form.register('name')} />
        </Field>

        <Field label="Email" error={form.formState.errors.email?.message}>
          <TextInput {...form.register('email')} />
        </Field>

        <Controller
          control={form.control}
          name="phoneNumber"
          render={({ field, fieldState }) => (
            <Field label="Phone Number" error={fieldState.error?.message}>
              <PhoneInput value={field.value} onChange={field.onChange} />
            </Field>
          )}
        />
      </div>
    </ModalForm>
  )
}

export default AddContactModal
